/**
 * Make the signed media URLs the ones the client reads (AUTHZ-02 E2).
 *
 * From E3 on, every serialized MediaObject carries `signedContentUrl`, `signedContentUrlSmall`,
 * `signedContentUrlMedium` and `signedContentUrlLarge` next to the unchanged, unsigned
 * `contentUrl*` fields, and E4 makes `/media` refuse a request without a valid signature. Every
 * reader in the client — rendering, the prefetch, the offline caches, the "is this the same
 * file?" comparisons — reads `contentUrl*`. Rather than teaching each of them a second field
 * family, every point where server JSON enters the client overwrites `contentUrl<Suffix>` with
 * `signedContentUrl<Suffix>` before anything else sees the body: the axios response interceptor,
 * the offline queue's replay response, the direct `fetch().json()` calls that read media fields,
 * Mercure messages, and the persisted React Query cache on restore.
 *
 * Contract (pinned by `__tests__/adoptSignedMediaUrls.test.ts`):
 * - PRESENCE decides, not truthiness. An own property `signedContentUrl<Suffix>` is copied into
 *   `contentUrl<Suffix>` whatever its value, `null` and `''` included: once the server sends the
 *   signed field it is the server's answer for that variant, and keeping the unsigned URL beside
 *   a `null` would hand the reader the one URL E4 refuses. An absent signed field leaves its
 *   `contentUrl<Suffix>` untouched — which is why this is a no-op against today's API.
 * - The `signedContentUrl*` fields themselves are kept. The write target always starts with
 *   `contentUrl`, so no key in the payload can steer it (a JSON `__proto__` key included).
 * - Only arrays and plain objects (prototype `Object.prototype` or `null`) are walked. Class
 *   instances, `Blob`, `Date`, `Map` and the like are left alone, and so is everything inside
 *   them — server JSON never produces them, so meeting one means the value is not a payload.
 * - The walk is in place and iterative: no recursion, so no stack limit on a deep payload. Each
 *   object is visited once, so a shared reference (the persisted cache can hold one object
 *   under two query keys) costs nothing extra and a cycle terminates.
 *
 * Dependency-free on purpose.
 */
const SIGNED_PREFIX = 'signedContentUrl'
const UNSIGNED_PREFIX = 'contentUrl'

/** Adopts in place and returns its argument, so a call can wrap the expression it adapts. */
export function adoptSignedMediaUrls<T>(value: T): T {
  adoptInPlace(value)
  return value
}

/**
 * The same adoption for a JSON text, for transports that hand every consumer the raw string
 * (Mercure: each subscriber parses `event.data` itself). Returns the input string itself when
 * nothing was adopted — the common case, decided without parsing when the text cannot contain a
 * signed field — and when the text is not JSON at all (a comment-removal event is a bare IRI).
 */
export function adoptSignedMediaUrlsInJson(text: string): string {
  if (!text.includes(SIGNED_PREFIX)) return text
  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch {
    return text
  }
  return adoptInPlace(parsed) === 0 ? text : JSON.stringify(parsed)
}

/** Walks `root` and returns how many `contentUrl<Suffix>` fields it overwrote. */
function adoptInPlace(root: unknown): number {
  if (!isWalkable(root)) return 0

  let adopted = 0
  const pending: object[] = [root]
  const visited = new Set<object>()
  while (pending.length > 0) {
    const node = pending.pop() as object
    if (visited.has(node)) continue
    visited.add(node)

    if (Array.isArray(node)) {
      for (const item of node) {
        if (isWalkable(item)) pending.push(item)
      }
      continue
    }

    const record = node as Record<string, unknown>
    for (const key of Object.keys(record)) {
      const value = record[key]
      if (key.startsWith(SIGNED_PREFIX)) {
        record[UNSIGNED_PREFIX + key.slice(SIGNED_PREFIX.length)] = value
        adopted += 1
      }
      if (isWalkable(value)) pending.push(value)
    }
  }
  return adopted
}

function isWalkable(value: unknown): value is object {
  if (typeof value !== 'object' || value === null) return false
  if (Array.isArray(value)) return true
  const prototype: unknown = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}
